// src/services/auth/authAccount.ts
import { supabase } from '@/lib/supabase';
import { checkSubscriptionStatusService } from './authSession';
import { onboardInviteEdge } from './authInvite';
import * as Sentry from '@sentry/react';

type LoginDeps = {
    setUser: (u: any | null) => void;
    setOrganization: (o: any | null) => void;
    setIsAuthenticated: (v: boolean) => void;
    setIsPaid: (v: boolean) => void;
    setSubscriptionPlan: (plan: string | null) => void;
    setLoading: (v: boolean) => void;
    setSubscriptionLoading: (v: boolean) => void;
};

type CreateUserDeps = {
    setUser: (u: any | null) => void;
    setIsAuthenticated: (v: boolean) => void;
    setLoading: (v: boolean) => void;
};

type CompleteRegistrationDeps = {
    user: any | null;
    setUser: (u: any | null) => void;
    setOrganization: (o: any | null) => void;
    setIsAuthenticated: (v: boolean) => void;
    setLoading: (v: boolean) => void;
};

type LogoutDeps = {
    setUser: (u: any | null) => void;
    setOrganization: (o: any | null) => void;
    setIsAuthenticated: (v: boolean) => void;
    setIsPaid: (v: boolean) => void;
};

type UpdateOrgDeps = {
    organization: any | null;
    setOrganization: (o: any | null) => void;
};

type AuthResult = { success: boolean; error: string; redirectTo?: string };

// --- LOGIN SERVICE ---
export const loginService = async (
    credentials: { email: string; password: string; inviteToken?: string },
    {
        setUser,
        setOrganization,
        setIsAuthenticated,
        setIsPaid,
        setSubscriptionPlan,
        setLoading,
        setSubscriptionLoading,
    }: LoginDeps,
): Promise<AuthResult> => {
    try {
        setLoading(true);

        // 1. Autenticazione con Supabase Auth
        const { data, error } = await supabase.auth.signInWithPassword({
            email: credentials.email,
            password: credentials.password,
        });

        if (error) {
            if ((error as any).code === 'email_not_confirmed') {
                throw new Error('Conferma il tuo indirizzo email prima di proseguire');
            }
            if (error.message === 'Invalid login credentials') {
                throw new Error('Email o password non corretti');
            }
            throw new Error('Credenziali non valide');
        }

        if (!data.user) throw new Error('Utente non trovato');

        // =========================================================
        // NUOVO CASO: Login con Token Invito (Utente Esistente)
        // =========================================================
        if (credentials.inviteToken) {
            console.log("Login con inviteToken rilevato. Esecuzione onboarding...");

            // Verifica dati minimi
            const fullName = data.user.user_metadata?.full_name || '';
            const [firstName, ...rest] = fullName.split(' ');
            const lastName = rest.join(' ');

            await onboardInviteEdge({
                token: credentials.inviteToken,
                user_id: data.user.id,
                email: data.user.email || '',
                first_name: firstName || 'User',
                last_name: lastName || '',
                setUser,
                setOrganization,
                setIsAuthenticated,
                setIsPaid,
                setSubscriptionPlan,
                setSubscriptionLoading
            });

            return { success: true, error: '' };
        }

        // =========================================================
        // FLUSSO NORMALE (Login Dipendente/Admin già registrato)
        // =========================================================

        // 2. Recupero Profilo Hairdresser
        const { data: hairdresser, error: hairdresserError } = await supabase
            .from('Hairdressers')
            .select('*')
            .eq('id', data.user.id)
            .maybeSingle();

        if (hairdresserError) {
            console.error("Errore recupero Hairdresser:", hairdresserError);
            throw new Error("Errore nel recupero del profilo");
        }

        // Utente autenticato ma senza profilo Hairdresser (es. registrazione incompleta)
        if (!hairdresser) {

            setUser(data.user);
            setOrganization(null);
            setIsAuthenticated(true);
            setIsPaid(false);
            return { success: true, error: '', redirectTo: '/new-shop' };
        }

        // 3. Recupero Shop
        const { data: shop, error: shopError } = await supabase
            .from('Shops')
            .select('*')
            .eq('id', hairdresser.shop_id)
            .single();

        if (shopError || !shop) {
            await supabase.auth.signOut();
            throw new Error('Accesso non autorizzato: salone non trovato');
        }

        // 4. Aggiornamento Stato
        setUser(hairdresser);
        setOrganization(shop);
        setIsAuthenticated(true);

        // 5. Verifica Abbonamento
        // Questa chiamata imposta setIsPaid(false) se il record non esiste o è scaduto.
        await checkSubscriptionStatusService(shop.id, {
            setIsPaid,
            setSubscriptionPlan,
            setSubscriptionLoading,
        });

        return { success: true, error: '' };

    } catch (error: any) {
        // Reset stato in caso di errore
        setUser(null);
        setOrganization(null);
        setIsAuthenticated(false);
        setIsPaid(false);

        return {
            success: false,
            error: error?.message ?? 'Errore di login',
        };
    } finally {
        setLoading(false);
    }
};

export const createUserAuthService = async (
    userData: any,
    { setUser, setIsAuthenticated, setLoading }: CreateUserDeps,
): Promise<AuthResult> => {
    try {
        setLoading(true);

        // Tentativo di Sign Up
        const { data: authData, error: authError } = await supabase.auth.signUp({
            email: userData.email,
            password: userData.password,
            options: {
                emailRedirectTo: `${window.location.origin}/auth/callback?redirect_type=signup`,
                data: {
                    full_name: `${userData.first_name} ${userData.last_name}`,
                    email: userData.email,
                    accepted_terms: !!userData.accepted_terms,
                    consent_marketing: !!userData.consent_marketing,
                    privacy_policy_read: !!userData.privacy_policy_read
                },
            },
        });

        // GESTIONE ERRORE ESPLICITO
        if (authError) {
            // Controlliamo i codici o i messaggi tipici di Supabase per "User already exists"
            const msg = authError.message.toLowerCase();
            const code = (authError as any).code;

            if (code === 'user_already_exists' || msg.includes('already registered') || msg.includes('unique constraint')) {
                return {
                    success: false,
                    error: 'USER_EXISTS_LOGIN_REQUIRED'
                };
            }

            // Altri errori reali
            let errorMessage = 'Errore durante la registrazione';
            if (msg.includes('rate limit')) errorMessage = 'Troppi tentativi. Riprova più tardi.';
            throw new Error(errorMessage);
        }

        // GESTIONE ERRORE IMPLICITO
        if (authData.user && authData.user.identities && authData.user.identities.length === 0) {
            console.warn("Rilevato utente esistente (identities vuoto).");
            return { success: false, error: 'USER_EXISTS_LOGIN_REQUIRED' };
        }

        if (!authData?.user) throw new Error('Registrazione fallita');

        localStorage.setItem('clip_pending_email', userData.email);
        setUser(authData.user);

        console.log('[Auth] authData', authData);

        if (!authData.user.confirmed_at) {
            setIsAuthenticated(false);
            return { success: true, error: '' };
        }

        return { success: true, error: '', redirectTo: '/new-shop' };

    } catch (error: any) {
        Sentry.captureException(error, {
            tags: { feature: 'auth', action: 'create_user' },
            extra: { email: userData.email }
        });
        return {
            success: false,
            error: error?.message ?? 'Errore durante la registrazione',
        };
    } finally {
        setLoading(false);
    }
};

export const completeRegistrationService = async (
    userData: any,
    { user, setUser, setOrganization, setIsAuthenticated, setLoading }: CompleteRegistrationDeps,
): Promise<{ success: boolean; error: string; shopData?: any }> => {
    try {
        setLoading(true);

        let lat = userData.lat || null;
        let long = userData.long || null;

        const { data: result, error: rpcError } = await supabase.rpc('register_user_transaction', {
            p_user_data: { ...userData, id: user?.id, lat, long },
        });

        if (rpcError || !result?.success) {
            console.error("RPC Error:", rpcError || result?.error);
            throw new Error('Registrazione fallita: ' + (result?.error || 'Errore transazione'));
        }

        setUser(result.hairdresser);
        setOrganization(result.shop);
        setIsAuthenticated(true);

        return { success: true, error: '', shopData: result.shop };
    } catch (error: any) {
        Sentry.captureException(error, {
            tags: { feature: 'auth', action: 'complete_registration' },
            extra: { userId: user?.id }
        });
        return {
            success: false,
            error: error?.message ?? 'Errore durante la registrazione',
        };
    } finally {
        setLoading(false);
    }
};

export const logoutService = async ({
    setUser,
    setOrganization,
    setIsAuthenticated,
    setIsPaid,
}: LogoutDeps): Promise<void> => {
    try {
        const { error } = await supabase.auth.signOut();
        if (error) throw error;
        setUser(null);
        setOrganization(null);
        setIsAuthenticated(false);
        setIsPaid(false);
    } catch (error) {
        console.error('Logout error:', error);
    }
};

export const deleteCurrentAccount = async (userId: string, isPaid: boolean): Promise<void> => {
    try {
        if (!userId) throw new Error('Utente non autenticato');

        if (isPaid) throw new Error('Impossibile eliminare l\'account. Hai un abbonamento attivo. Annullalo prima di procedere.');

        // 1. Chiama la funzione RPC per eliminare il record Hairdresser e verificare se eliminare Auth
        const { data: rpcData, error: rpcError } = await supabase.rpc('delete_hairdresser_record', {
            p_hairdresser_id: userId
        });

        if (rpcError) {
            console.error('RPC Error - delete_hairdresser_record:', rpcError);
            throw new Error('Errore durante l\'eliminazione del profilo parrucchiere');
        }

        // 2. Se necessario, elimina anche l'account di autenticazione (Edge Function)
        if (rpcData && rpcData.should_delete_auth) {
            const { error: functionError } = await supabase.functions.invoke('delete-user-account', {
                method: 'POST',
            });

            if (functionError) {
                throw new Error(functionError.message || 'Errore durante l\'eliminazione dell\'account');
            }
        }

    } catch (error: any) {
        Sentry.captureException(error, {
            tags: { feature: 'account', action: 'delete_account' },
            extra: { userId, isPaid }
        });
        console.error('Service Error - deleteCurrentAccount:', error);
        throw error;
    }
};

export const updateOrganizationService = async (
    organizationData: any,
    { organization, setOrganization }: UpdateOrgDeps,
): Promise<{ success: boolean; error: string }> => {
    try {
        const { data: shop, error } = await supabase
            .from('Shops')
            .update(organizationData)
            .eq('id', organization?.id)
            .select()
            .single();

        if (error) {
            console.error(
                "Errore durante l'aggiornamento del negozio:",
                error,
            );
            throw new Error("Errore durante l'aggiornamento del salone");
        }

        setOrganization(shop);
        return { success: true, error: '' };
    } catch (error: any) {
        Sentry.captureException(error, {
            tags: { feature: 'account', action: 'update_organization' },
            extra: { organizationId: organization?.id, organizationData }
        });
        console.error(
            "Errore durante l'aggiornamento del negozio:",
            error,
        );
        return {
            success: false,
            error:
                error?.message ||
                "Errore durante l'aggiornamento del salone",
        };
    }
};